import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
    LAYOUT_HEIGHT, 
    DIVERT_RETURN_STATUS, 
    EXCLUDED_FLIGHT_STATUS 
} from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { countValuesByField, countAvgMinutesByTableau } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import CommonTableau from './CommonTableau';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const tableauViewOptions = [
    { value: "byDate", label: "by Date" },
    { value: "byDateDep", label: "by Date & Port" },
    { value: "byDateSector", label: "by Date & Sector" },
    { value: "byDateSectorType", label: "by Date & Sector Type" },
    { value: "byDateFlightType", label: "by Date & Flight Type" },
    { value: "byDateFlightReg", label: "by Date & Flight Reg" },
    { value: "byDateACType", label: "by Date & AC Type" },
    { value: "byDateCountry", label: "by Date & Country" },
    { value: "byDateFlightNo", label: "by Date & Flight No" },
    { value: "byDep", label: "by Port" },
    { value: "bySector", label: "by Sector" },
    { value: "bySectorType", label: "by Sector Type" },
    { value: "byFlightType", label: "by Flight Type" },
    { value: "byFlightReg", label: "by Flight Reg" },
    { value: "byFlightRegDep", label: "by Reg & Port" },
    { value: "byFlightRegSector", label: "by Reg & Sector" },
    { value: "byFlightRegSectorType", label: "by Reg & Sector Type" },
    { value: "byFlightRegFlightType", label: "by Reg & Flight Type" },
    { value: "byFlightRegCountry", label: "by Reg & Country" },
    { value: "byACType", label: "by AC Type" },
    { value: "byACTypeDep", label: "by AC & Port" },
    { value: "byACTypeSector", label: "by AC & Sector" },
    { value: "byACTypeSectorType", label: "by AC & Sector Type" },
    { value: "byACTypeFlightType", label: "by AC & Flight Type" },
    { value: "byACTypeCountry", label: "by AC & Country" },
    { value: "byCountry", label: "by Country" },
    { value: "byFlightNo", label: "by Flight No" },
];

const viewOptions = {
    chart: [
        { value: "taxiOutByFlightDate", label: "by Date" },
        { value: "taxiOutByDep", label: "by Port" },
        { value: "taxiOutBySector", label: "by Sector" },
        { value: "taxiOutBySectorType", label: "by Sector Type" },
        { value: "taxiOutByFlightType", label: "by Flight Type" },
        { value: "taxiOutByFlightReg", label: "by Flight Reg" },
        { value: "taxiOutByACType", label: "by AC Type" },
        { value: "taxiOutByCountry", label: "by Country" },
        { value: "taxiOutByFlightNo", label: "by Flight No" },
    ],
    tableau: tableauViewOptions,
};

const TaxiOutChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);  
    
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('taxiOutByFlightDate');
    const [flights, setFlights] = useState([]); 
    const [data, setData] = useState({
        taxiOutByFlightDate: [],
        taxiOutByDep: [],
        taxiOutBySector: [],
        taxiOutBySectorType: [],
        taxiOutByFlightType: [],
        taxiOutByFlightReg: [],
        taxiOutByACType: [],  
        taxiOutByCountry: [],  
        taxiOutByFlightNo: [],
    });

    const flightsData = useSelector((state) => state.flights); 
    
    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true); 
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);
                
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);       

    useEffect(() => {        
        const results = {
            taxiOutByFlightDate: countValuesByField(filteredFlights, 'taxiOut', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'),
            taxiOutByDep: countValuesByField(filteredFlights, 'taxiOut', 'dep'),
            taxiOutBySector: countValuesByField(filteredFlights, 'taxiOut', 'sector'),
            taxiOutBySectorType: countValuesByField(filteredFlights, 'taxiOut', 'sectorType'),
            taxiOutByFlightType: countValuesByField(filteredFlights, 'taxiOut', 'flightType'),
            taxiOutByFlightReg: countValuesByField(filteredFlights, 'taxiOut', 'flightReg'),
            taxiOutByACType: countValuesByField(filteredFlights, 'taxiOut', 'acType'),
            taxiOutByCountry: countValuesByField(filteredFlights, 'taxiOut', 'depCountryName'),
            taxiOutByFlightNo: countValuesByField(filteredFlights, 'taxiOut', 'flightNo'),
        };            
        setData(results);
    }, [timeMode, filteredFlights]); 
    
    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view) ? view : options[0]?.value || '';
    }, [view, displayType]);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];
        const fieldMap = {
            byDate:                [flightDate, null],
            byDateDep:             [flightDate, 'dep'],
            byDateSector:          [flightDate, 'sector'],
            byDateSectorType:      [flightDate, 'sectorType'],
            byDateFlightType:      [flightDate, 'flightType'],
            byDateFlightReg:       [flightDate, 'flightReg'],
            byDateACType:          [flightDate, 'acType'],
            byDateCountry:         [flightDate, 'depCountryName'],
            byDateFlightNo:        [flightDate, 'flightNo'],
            byDep:                 ['dep', null],
            bySector:              ['sector', null],
            bySectorType:          ['sectorType', null],
            byFlightType:          ['flightType', null],
            byFlightReg:           ['flightReg', null],
            byFlightRegDep:        ['flightReg', 'dep'],
            byFlightRegSector:     ['flightReg', 'sector'],
            byFlightRegSectorType: ['flightReg', 'sectorType'],
            byFlightRegFlightType: ['flightReg', 'flightType'],
            byFlightRegCountry:    ['flightReg', 'depCountryName'],
            byACType:              ['acType', null],
            byACTypeDep:           ['acType', 'dep'],
            byACTypeSector:        ['acType', 'sector'],
            byACTypeSectorType:    ['acType', 'sectorType'],
            byACTypeFlightType:    ['acType', 'flightType'],
            byACTypeCountry:       ['acType', 'depCountryName'],
            byCountry:             ['depCountryName', null],
            byFlightNo:            ['flightNo', null],
        };
        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countAvgMinutesByTableau(f, fields[0], fields[1], 'taxiOut', 'Taxi Out', (_, v) => v > 0);
    }, [displayType, safeView, timeMode, filteredFlights]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);
    
    const handleOpen = () => {
        setChartOpen(true);
    };

    const handleClose = () => {
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Taxi Out ${period}`;

    const chartMap = {
        taxiOutByFlightDate: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Date", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByDep: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Port", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutBySector: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector", leftLegend: "Minutes", bottomOffSet: 10, bottomLegendOffSet: 10, digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutBySectorType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector Type", leftLegend: "Minutes", isHorizontalBottomLegend: true, bottomOffSet: -30, bottomLegendOffSet: -30, digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByFlightType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Type", leftLegend: "Minutes", isHorizontalBottomLegend: !isMobile ? true : false, bottomOffSet: !isMobile ? -30 : 50, bottomLegendOffSet: !isMobile ? -30 : 50, digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByFlightReg: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Reg", leftLegend: "Minutes", bottomOffSet: 5, bottomLegendOffSet: 5, digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByACType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "AC Type", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByCountry: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Country", leftLegend: "Minutes", digit: 0, bottomOffSet: isMobile ? 55 : 45, bottomLegendOffSet: isMobile ? 45 : 45, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        taxiOutByFlightNo: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight No", leftLegend: "Minutes", bottomOffSet: 5, bottomLegendOffSet: 5, digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
    };
    
    const renderChart = (view, customStyles) => {
        const chart = chartMap[view];
        return chart ? (
          <Box sx={{ ...styles, ...customStyles }}>
            <chart.component data={data[view]} {...chart.props} />
          </Box>
        ) : (
          <Box sx={{ ...styles, ...customStyles }}>                                
            <Typography>No charts available</Typography>
          </Box>
        );
    };


    return (
        <Box className="main-container-box">     
            <FlexBetween>
                <Header
                    title="Taxi Out"
                    subtitle={loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl size="small" sx={{ width: isMobile ? '6rem' : '8rem' }}>
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ width: isMobile ? '7rem' : '12rem' }}>
                            <InputLabel id="taxi-out-view-label">View</InputLabel>
                            <Select
                                labelId="taxi-out-view-label"
                                label="View"
                                value={safeView}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>           
            {loading ? (
                <FlexCenter sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"                   
                >
                    <Box
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                        onDoubleClick={displayType === 'chart' ? handleOpen : undefined}
                    >
                        {displayType === 'chart'
                            ? renderChart(safeView, { minHeight: LAYOUT_HEIGHT })
                            : <Box sx={{ ...styles, minHeight: LAYOUT_HEIGHT }}>                                
                                <Box height="100%" width="100%">
                                    <CommonTableau data={tableauData} />
                                </Box>
                              </Box>
                        }
                    </Box>

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && displayType === 'chart' && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleClose}
                    chartComponent={renderChart(safeView, {})}
                />
            )}
        </Box>
    );
};

export default TaxiOutChart;
